import Swiper from 'swiper';
import { getNavButton } from '@/partials/el.NavButton';
import { Navigation } from 'swiper/modules';
import { showToast } from '@/partials/el.Utils.js';
import { trackAddedToWishlist, trackRemovedFromWishlist } from '@/partials/el.WishlistTracking.js';

elabs.YouMayAlsoLike = class {
  constructor(element) {
    this.view = element;

    const { currencySymbol, currencyIsoCode, countryIsoCode, productIds, pageProductId, recommendationType, limit } =
      this.view.dataset;

    this.currencySymbol = currencySymbol;
    this.currencyIsoCode = currencyIsoCode;
    this.countryIsoCode = countryIsoCode;
    this.productIds = JSON.parse(productIds || '[]');
    this.pageProductId = pageProductId;
    this.recommendationType = recommendationType;
    this.limit = parseInt(limit, 10);
    this.swiperSlider = null;
    this.activeProductSlider = null;

    this.itemTemplate = this.view.querySelector('.el-you-may-also-like__item-template');
    this.itemsContainer = this.view.querySelector('.js-el-you-may-also-like-items');
    this.swiperContainer = this.view.querySelector('.swiper');

    if (!this.itemsContainer || !this.itemTemplate || !this.swiperContainer) {
      console.error('YouMayAlsoLike: Could not find all required elements.');
      return;
    }

    this.init();
  }

  init() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);

    this.initProductSliderListeners();
    this.initWishlistListeners();

    this.fetchAndRenderProducts();

    const attachClickListeners = (slideElement) => {
      const productLink = slideElement.querySelector('.js-product-link');
      if (!productLink) {
        return;
      }

      slideElement.addEventListener('click', (event) => {
        if (event.target.closest('.el-circular-nav-button')) {
          return;
        }
        if (event.target.closest('.js-product-card-wishlist')) {
          return;
        }
        if (event.target.closest('a') === productLink) {
          return;
        }
        if (productLink) {
          if (event.metaKey || event.ctrlKey) {
            window.open(productLink.href, '_blank');
          } else {
            productLink.click();
          }
        }
      });
    };

    if (this.recommendationType === 'hand-picked-products') {
      const existingSlides = this.itemsContainer.querySelectorAll('.el-you-may-also-like-item');
      existingSlides.forEach(attachClickListeners);
    } else {
      const observer = new MutationObserver((mutationsList, obs) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.matches('.el-you-may-also-like-item')) {
                attachClickListeners(node);
              }
            });
          }
        }
      });
      observer.observe(this.itemsContainer, { childList: true, subtree: true });
    }
  }

  async fetchAndRenderProducts() {
    if (this.recommendationType === 'hand-picked-products') {
      // Update wishlist states for pre-rendered products
      this._waitForSDKAndUpdateStates();
      this.initSlider();
      return;
    }

    const isRecentlyViewed = this.recommendationType === 'recently-viewed';
    const requestProductIds = isRecentlyViewed ? this.getRecentlyViewedIds() : this.productIds;

    if (isRecentlyViewed && requestProductIds.length === 0) {
      this.view.style.display = 'none';
      return;
    }

    const excluded = [];
    const API_URL = 'https://services.mybcapps.com/discovery/recommend';
    const payload = {
      shop: window.Shopify.shop || 'sophiawebster.myshopify.com',
      recommendationType: this.recommendationType,
      productIds: requestProductIds,
      debug: true,
      limit: this.limit,
      currency: this.currencyIsoCode?.toUpperCase() || 'GBP',
      currency_rate: 1,
      country: this.countryIsoCode || 'GB',
      return_all_currency_fields: false,
    };

    const usesSameCollectionRule =
      this.recommendationType === 'frequently-bought-together' || this.recommendationType === 'related-items';
    if (usesSameCollectionRule) {
      payload.ruleBased = [{ type: 'sameCollection', status: true, excluded }];
    }

    if (this.recommendationType === 'trending-products') {
      payload.calculatedBasedOn = 'view-events';
      payload.rangeOfTime = '7-day';
    }

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let { products } = data;

      const idsToExclude = isRecentlyViewed ? [...excluded] : [...this.productIds.map(String), ...excluded];
      products = products.filter((item) => !idsToExclude.includes(String(item.id)));

      const fragment = document.createDocumentFragment();
      for (const item of products) {
        if (String(item.id) === this.pageProductId) {
          continue;
        }
        try {
          const productElement = await this.createItemElement(item);
          if (productElement) {
            fragment.appendChild(productElement);
          }
        } catch (itemError) {
          console.error('YouMayAlsoLike: failed to render recommended product', item?.id, itemError);
        }
      }
      this.itemsContainer.appendChild(fragment);

      // Update wishlist states after products are rendered
      this._waitForSDKAndUpdateStates();

      this.initSlider();
    } catch (e) {
      console.error('Error getting recommendations in YouMayAlsoLike:', e);
    }
  }

  getRecentlyViewedIds() {
    try {
      const stored = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      return stored.map(Number).filter((id) => !Number.isNaN(id));
    } catch {
      return [];
    }
  }

  initSlider() {
    const slides = this.itemsContainer.querySelectorAll('.swiper-slide');

    if (slides.length === 0) {
      this.view.style.display = 'none';
      return;
    }

    this.swiperSlider = new Swiper(this.swiperContainer, {
      modules: [Navigation],
      slidesPerView: 2,
      speed: 700,
      spaceBetween: 6,
      observer: true,
      loop: slides.length > 2,
      navigation: {
        nextEl: '.el-you-may-also-like__nav-button-container--next',
        prevEl: '.el-you-may-also-like__nav-button-container--prev',
      },
      on: {
        init: () => {
          this.view.classList.remove('el-is-initializing');
        },
        afterInit: (swiper) => this.updateAllowTouchMove(swiper),
        resize: (swiper) => this.updateAllowTouchMove(swiper),
        observerUpdate: (swiper) => this.updateAllowTouchMove(swiper),
        slideChangeTransitionEnd: (swiper) => this.updateAllowTouchMove(swiper),
      },
      breakpoints: {
        768: { slidesPerView: 3, spaceBetween: 18, loop: slides.length > 3 },
        1360: { slidesPerView: 4, spaceBetween: 50, loop: slides.length > 4 },
        1700: { slidesPerView: 5, loop: slides.length > 5 },
        2040: { slidesPerView: 6, loop: slides.length > 6 },
        2380: { slidesPerView: 7, loop: slides.length > 7 },
        2720: { slidesPerView: 8, loop: slides.length > 8 },
      },
    });

    this.view
      .querySelectorAll('.el-circular-nav-button--prev, .el-slideshow__image-nav-left-button')
      .forEach((button) => {
        button.addEventListener('click', () => this.swiperSlider.slidePrev());
      });

    this.view
      .querySelectorAll('.el-circular-nav-button--next, .el-slideshow__image-nav-right-button')
      .forEach((button) => {
        button.addEventListener('click', () => this.swiperSlider.slideNext());
      });
  }

  updateAllowTouchMove(swiper) {
    const allVisible = this.checkAllSlidesVisible(swiper);
    swiper.allowTouchMove = !allVisible;
    swiper.allowSlidePrev = !allVisible;
    swiper.allowSlideNext = !allVisible;
    this.toggleButtonDisplay(allVisible);
  }

  checkAllSlidesVisible(swiper) {
    const originalSlides = Array.from(swiper.slides).filter(
      (slide) => !slide.classList.contains('swiper-slide-duplicate'),
    );
    return originalSlides.every((slide) => slide.classList.contains('swiper-slide-fully-visible'));
  }

  handleResize() {
    if (this.swiperSlider && !this.swiperSlider.destroyed) {
      this.swiperSlider.destroy(true, true);
    }
    this.initSlider();
  }

  toggleButtonDisplay(allVisible) {
    const buttons = this.view.querySelectorAll('.el-circular-nav-button');
    buttons.forEach((button) => {
      button.style.display = allVisible ? 'none' : '';
    });
  }

  async createItemElement(item) {
    const {
      id,
      images_info,
      title,
      handle,
      metafields,
      price_min,
      compare_at_price_min,
      variants,
      options,
      options_with_values,
    } = item;
    const initialImageMeta = metafields.find(
      (m) => m.namespace === 'sophiawebster' && m.key === 'collection_page_initial_image',
    );
    const hoverImageMeta = metafields.find(
      (m) => m.namespace === 'sophiawebster' && m.key === 'collection_page_hover_image',
    );
    const vimeoMetafield = metafields.find((m) => m.key === 'vimeo_video_id_collection_page');
    const vimeoId = vimeoMetafield ? vimeoMetafield.value : null;
    const initialImageIndex = initialImageMeta ? parseInt(initialImageMeta.value, 10) : 0;
    const hoverImageIndex = hoverImageMeta ? parseInt(hoverImageMeta.value, 10) : 0;
    const imageCount = images_info.length;

    let initial_image = images_info[0];
    if (initialImageIndex > 0 && initialImageIndex <= imageCount) {
      initial_image = images_info[initialImageIndex - 1];
    }

    let hover_image = null;
    if (imageCount > 1) {
      if (vimeoId) {
        hover_image = images_info[0];
      } else {
        hover_image = images_info[1];
      }
      if (hoverImageIndex > 0 && hoverImageIndex <= imageCount) {
        hover_image = images_info[hoverImageIndex - 1];
      }
    }

    const url = `/products/${handle}`;
    const clone = this.itemTemplate.content.cloneNode(true);
    const slideElement = clone.querySelector('.el-you-may-also-like-item');
    const highlightBadge = slideElement.querySelector('.el-you-may-also-like-item__custom-badge--highlight-text');
    const saleBadge = slideElement.querySelector('.el-you-may-also-like-item__custom-badge--sale-percent');

    // Set product data for wishlist
    slideElement.dataset.productId = id;
    const firstVariant = variants?.find((v) => v.available) || variants?.[0];
    if (firstVariant) {
      slideElement.dataset.firstVariantId = firstVariant.id;
    }
    // Check if product has size variants (like PDP logic)
    // Use options_with_values (from recommendations API) or options (from Shopify API)
    const optionsToCheck = options_with_values || options || [];
    const hasSizeOption = optionsToCheck.some((opt) => {
      const optName = typeof opt === 'string' ? opt : opt?.name || opt?.label;
      return optName?.toLowerCase() === 'size';
    });
    const hasSizeVariants = hasSizeOption && variants?.length > 1;
    slideElement.dataset.hasSizeVariants = hasSizeVariants ? 'true' : 'false';
    slideElement.dataset.price = price_min || '0';

    const highlightMetafield = metafields.find(
      (meta) => meta.key === 'product_highlight_text' && meta.namespace === 'sophiawebster',
    );
    if (highlightMetafield && highlightMetafield.value) {
      highlightBadge.textContent = highlightMetafield.value;
    } else {
      highlightBadge.style.display = 'none';
    }

    const price = parseFloat(price_min);
    const comparePrice = parseFloat(compare_at_price_min);

    if (comparePrice > price) {
      const salePercent = Math.round(((comparePrice - price) / comparePrice) * 100);
      saleBadge.textContent = `Save ${salePercent}%`;
    } else {
      saleBadge.style.display = 'none';
    }

    slideElement.querySelector('.js-product-link').href = url;
    slideElement.querySelector('.js-title').textContent = title;

    const mainImageEl = slideElement.querySelector('.js-main-image');
    const videoContainerEl = slideElement.querySelector('.js-video-container');

    if (!vimeoId && initial_image) {
      const firstImage = initial_image;
      const widths = [320, 400, 550, 700, 900, 1050, 1200, 1400];
      const baseUrl = firstImage.src.split('?')[0];
      const vParam = firstImage.src.split('v=')[1] ? `v=${firstImage.src.split('v=')[1].split('&')[0]}` : '';

      mainImageEl.srcset = widths.map((w) => `${baseUrl}?${vParam}&width=${w} ${w}w`).join(', ');
      mainImageEl.sizes =
        '(min-width: 2720px) calc(100vw / 8), (min-width: 2380px) calc(100vw / 7), (min-width: 2040px) calc(100vw / 6), (min-width: 1700px) calc(100vw / 5), (min-width: 1360px) 25vw, (min-width: 768px) 33.3vw, 50vw';
      mainImageEl.src = `${baseUrl}?${vParam}&width=400`;
      mainImageEl.alt = firstImage.alt || title;
      mainImageEl.width = firstImage.width;
      mainImageEl.height = firstImage.height;
    } else if (vimeoId) {
      mainImageEl.style.display = 'none';
      videoContainerEl.style.display = 'block';
      videoContainerEl.innerHTML = `
        <iframe
          src="https://player.vimeo.com/video/${vimeoId}?background=1&loop=1&muted=1&dnt=1"
          frameborder="0" allow="autoplay; fullscreen" width="100%" height="100%">
        </iframe>`;
      if (initial_image) {
        videoContainerEl.style.aspectRatio = `${initial_image.width || 1}/${initial_image.height || 1}`;
      }
    } else {
      mainImageEl.style.display = 'none';
      slideElement.querySelector('.js-placeholder-image').classList.remove('hidden');
    }

    if (images_info && images_info.length > 0) {
      const sliderDataEl = slideElement.querySelector('.js-slider-data');
      sliderDataEl.dataset.imagesJson = JSON.stringify(images_info);
      if (initial_image) {
        sliderDataEl.dataset.initialImageSrc = initial_image.src;
      }
      if (hover_image) {
        sliderDataEl.dataset.hoverImageSrc = hover_image.src;
      }
      if (vimeoId) {
        sliderDataEl.dataset.vimeoId = vimeoId;
      }
    }

    const imageToPreload = hover_image;

    if (imageToPreload) {
      const link = document.createElement('link');
      const widths = [320, 400, 550, 700, 900, 1050, 1200, 1400];
      const baseUrl = imageToPreload.src.split('?')[0];
      const vParam = imageToPreload.src.split('v=')[1] ? `v=${imageToPreload.src.split('v=')[1].split('&')[0]}` : '';

      link.rel = 'preload';
      link.as = 'image';
      link.href = `${baseUrl}?${vParam}&width=400`;
      link.imageSrcset = widths.map((w) => `${baseUrl}?${vParam}&width=${w} ${w}w`).join(', ');
      link.imageSizes =
        '(min-width: 2720px) calc(100vw / 8), (min-width: 2380px) calc(100vw / 7), (min-width: 2040px) calc(100vw / 6), (min-width: 1700px) calc(100vw / 5), (min-width: 1360px) 25vw, (min-width: 768px) 33.3vw, 50vw';
      document.head.appendChild(link);
    }

    this.renderPrice(slideElement, item);
    return clone;
  }

  renderPrice(element, item) {
    const priceOneVariantEl = element.querySelector('.js-price-product-with-one-variant');
    const compareAtPrice = item.compare_at_price_min;
    const price = item.price_min;

    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
      const compareEl = priceOneVariantEl.querySelector('.el-price__compare-at-price');
      compareEl.querySelector('.compare_at_price').textContent = this.priceWithSymbol(compareAtPrice);
      compareEl.querySelector('.el-price__discounted-price').textContent = this.priceWithSymbol(price);
      if (price) {
        compareEl.classList.remove('hidden');
      }
    } else if (price) {
      const defaultEl = priceOneVariantEl.querySelector('.el-price__price');
      defaultEl.textContent = this.priceWithSymbol(price);
      defaultEl.classList.remove('hidden');
    }
    if (price) {
      priceOneVariantEl.classList.remove('hidden');
    }
  }

  priceWithSymbol(value) {
    return `${this.currencySymbol}${value}`;
  }

  initWishlistListeners() {
    // Handle wishlist button clicks
    this.view.addEventListener('click', async (e) => {
      const wishlistBtn = e.target.closest('.js-product-card-wishlist');
      if (!wishlistBtn) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const slideElement = wishlistBtn.closest('.el-you-may-also-like-item');
      if (!slideElement) {
        return;
      }

      const productId = slideElement.dataset.productId;
      const variantId = slideElement.dataset.firstVariantId;
      const hasSizeVariants = slideElement.dataset.hasSizeVariants === 'true';
      const title = slideElement.querySelector('.js-title')?.textContent || '';
      const productLink = slideElement.querySelector('.js-product-link')?.href || '';
      const image = slideElement.querySelector('.js-main-image')?.src || '';

      if (!productId || !variantId) {
        return;
      }

      const isInWishlist = wishlistBtn.classList.contains('is-in-wishlist');

      const heartIcon = window.elabsAssets?.wishlistIconOutlineWhite || '';

      // Optimistic UI + toast (don't wait for network)
      if (isInWishlist) {
        wishlistBtn.classList.remove('is-in-wishlist');
        showToast(`${title} has been removed from your wishlist`, heartIcon);
      } else {
        wishlistBtn.classList.add('is-in-wishlist');
        showToast(`${title} has been added to your wishlist`, heartIcon);
      }

      try {
        const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

        if (isInWishlist) {
          // Find the actual variant ID in wishlist (might differ from firstVariantId)
          const items = await window.WishListHero_SDK.GetWishListItems();
          const wishlistItem = items.find((item) => String(item.ProductId) === String(productId));

          if (wishlistItem) {
            await window.WishListHero_SDK.DeleteWishListItem({
              ProductVariantId: parseInt(wishlistItem.ProductVariantId),
              WishlistHash: wishlistHash,
            });

            // Track removal event (fire-and-forget)
            trackRemovedFromWishlist({
              productId: wishlistItem.ProductId,
              variantId: wishlistItem.ProductVariantId,
              name: title,
              price: wishlistItem.Price || wishlistItem.ProductPrice || 0,
              url: productLink,
              image: image,
            });
          }
        } else {
          // Add NO_VARIANT_MARKER only if product has size variants (like PDP logic)
          const NO_VARIANT_MARKER = '   ';
          const productTitle = hasSizeVariants ? title + NO_VARIANT_MARKER : title;

          await window.WishListHero_SDK.AddWishListItem({
            ProductId: parseInt(productId),
            ProductTitle: productTitle,
            ProductPrice: 0,
            ProductLink: productLink,
            ProductVariantId: parseInt(variantId),
            ProductImage: image,
            WishlistHash: wishlistHash,
          });

          // Track added event (fire-and-forget)
          trackAddedToWishlist({
            productId: productId,
            variantId: variantId,
            name: title,
            price: parseFloat(slideElement.dataset.price) || 0,
            url: productLink,
            image: image,
          });
        }

        // Dispatch update event
        document.dispatchEvent(new CustomEvent('wishlist:updated'));
      } catch (error) {
        console.error('Error updating wishlist:', error);

        // Revert optimistic UI + show error toast
        if (isInWishlist) {
          wishlistBtn.classList.add('is-in-wishlist');
        } else {
          wishlistBtn.classList.remove('is-in-wishlist');
        }
        showToast('Could not update wishlist. Please try again.', heartIcon);
      }
    });

    // Update wishlist states when wishlist changes
    document.addEventListener('wishlist:updated', () => this.updateWishlistStates());
  }

  /**
   * Wait for SDK to be ready before updating wishlist states
   * Called after products are rendered in DOM
   */
  _waitForSDKAndUpdateStates() {
    if (window.WishListHero_SDK) {
      this.updateWishlistStates();
    } else {
      document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => {
        this.updateWishlistStates();
      });
    }
  }

  async updateWishlistStates() {
    if (!window.WishListHero_SDK) {
      return;
    }

    try {
      const items = await window.WishListHero_SDK.GetWishListItems();
      if (!items || items.length === 0) {
        // No items in wishlist, remove all is-in-wishlist classes
        const wishlistBtns = this.view.querySelectorAll('.js-product-card-wishlist');
        wishlistBtns.forEach((btn) => btn.classList.remove('is-in-wishlist'));
        return;
      }

      // Check by ProductId since variant might differ
      const productIds = items.map((item) => String(item.ProductId));

      const wishlistBtns = this.view.querySelectorAll('.js-product-card-wishlist');
      wishlistBtns.forEach((btn) => {
        const slideElement = btn.closest('.el-you-may-also-like-item');
        const productId = slideElement?.dataset.productId;

        if (productId && productIds.includes(String(productId))) {
          btn.classList.add('is-in-wishlist');
        } else {
          btn.classList.remove('is-in-wishlist');
        }
      });
    } catch (error) {
      console.error('Error updating wishlist states:', error);
    }
  }

  initProductSliderListeners() {
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleTouchInteraction = this.handleTouchInteraction.bind(this);

    this.view.addEventListener('mouseover', this.handleMouseEnter);
    this.view.addEventListener('mouseout', this.handleMouseLeave);
    this.view.addEventListener('touchstart', this.handleTouchInteraction, { passive: true });
  }

  handleTouchInteraction(event) {
    const touchedWrapper = event.target.closest('.js-media-wrapper');

    if (this.activeProductSlider && !this.activeProductSlider.wrapper.contains(event.target)) {
      this.destroyProductSlider();
    }

    if (touchedWrapper) {
      if (!this.activeProductSlider || this.activeProductSlider.wrapper !== touchedWrapper) {
        this.destroyProductSlider();
        const sliderDataSource = touchedWrapper.querySelector('.js-slider-data');
        if (sliderDataSource && sliderDataSource.dataset.imagesJson) {
          this.createProductSlider(touchedWrapper);
        }
      }
    }
  }

  handleMouseEnter(event) {
    const mediaWrapper = event.target.closest('.js-media-wrapper');
    if (mediaWrapper && (!this.activeProductSlider || this.activeProductSlider.wrapper !== mediaWrapper)) {
      this.destroyProductSlider();
      this.createProductSlider(mediaWrapper);
    }
  }

  handleMouseLeave(event) {
    const mediaWrapper = event.target.closest('.js-media-wrapper');
    if (mediaWrapper && !mediaWrapper.contains(event.relatedTarget)) {
      this.destroyProductSlider();
    }
  }

  createProductSlider(wrapper) {
    const dataEl = wrapper.querySelector('.js-slider-data');
    if (!dataEl || !dataEl.dataset.imagesJson) {
      return;
    }

    // Get product link URL for wrapping slides
    const slideElement = wrapper.closest('.el-you-may-also-like-item');
    const productLinkEl = slideElement?.querySelector('.js-product-link');
    const productUrl = productLinkEl ? productLinkEl.href : '#';
    const productTitle = slideElement?.querySelector('.js-title')?.textContent || '';

    const images = JSON.parse(dataEl.dataset.imagesJson);
    const hasVimeoVideo = !!dataEl.dataset.vimeoId;
    const initialImageSrc = dataEl.dataset.initialImageSrc;
    const hoverImageSrc = dataEl.dataset.hoverImageSrc;

    let sortedImages = [...images];

    if (hoverImageSrc && hoverImageSrc !== initialImageSrc) {
      const hoverImage = sortedImages.find((img) => img.src === hoverImageSrc);
      const initialImage = sortedImages.find((img) => img.src === initialImageSrc);

      if (hoverImage && initialImage) {
        const otherImages = sortedImages.filter((img) => img.src !== hoverImageSrc && img.src !== initialImageSrc);

        if (hasVimeoVideo) {
          sortedImages = [hoverImage, initialImage, ...otherImages];
        } else {
          sortedImages = [hoverImage, ...otherImages, initialImage];
        }
      }
    }

    const shouldCreateSlider =
      (hasVimeoVideo && sortedImages.length > 0) || (!hasVimeoVideo && sortedImages.length > 1);

    if (!shouldCreateSlider) {
      return;
    }

    wrapper.querySelector('.js-main-image')?.style.setProperty('display', 'none', 'important');
    wrapper.querySelector('.js-video-container')?.style.setProperty('display', 'none', 'important');

    const createResponsiveImageHtml = (image) => {
      if (!image || !image.src) {
        return '';
      }
      const widths = [320, 400, 550, 700, 900, 1050, 1200, 1400];
      const baseUrl = image.src.split('?')[0];
      const vParam = image.src.split('v=')[1] ? `v=${image.src.split('v=')[1].split('&')[0]}` : '';

      const srcset = widths.map((w) => `${baseUrl}?${vParam}&width=${w} ${w}w`).join(', ');
      const sizes = [
        '(min-width: 2720px) calc(100vw / 8)',
        '(min-width: 2380px) calc(100vw / 7)',
        '(min-width: 2040px) calc(100vw / 6)',
        '(min-width: 1700px) calc(100vw / 5)',
        '(min-width: 1360px) 25vw',
        '(min-width: 768px) 33.3vw',
        '50vw',
      ].join(', ');
      const fallbackSrc = `${baseUrl}?${vParam}&width=400`;
      const altText = image.alt ? image.alt.replace(/"/g, '&quot;') : '';

      return (
        `<img src="${fallbackSrc}" srcset="${srcset}" sizes="${sizes}" alt="${altText}" ` +
        `loading="lazy" width="${image.width}" height="${image.height}" draggable="false" ` +
        'style="width: 100%; height: 100%; user-select: none;"/>'
      );
    };

    const slidesHtml = sortedImages
      .map((img) => {
        const imageHtml = createResponsiveImageHtml(img);
        return (
          `<div class="swiper-slide"><a href="${productUrl}" aria-label="${productTitle}" ` +
          `class="el-product-slider-link" draggable="false">${imageHtml}</a></div>`
        );
      })
      .join('');

    const prevBtn = getNavButton({
      isFacingLeft: true,
      hasWhiteBorder: true,
      buttonClass: 'custom-swiper-button-prev',
    });
    const nextBtn = getNavButton({
      isFacingLeft: false,
      hasWhiteBorder: true,
      buttonClass: 'custom-swiper-button-next',
    });

    const sliderContainer = document.createElement('div');
    sliderContainer.className = 'swiper el-you-may-also-like-item__inner-swiper';
    sliderContainer.innerHTML = `
      <div class="swiper-wrapper">${slidesHtml}</div>
      <div class="el-circular-nav-button el-circular-nav-button--prev">${prevBtn}</div>
      <div class="el-circular-nav-button el-circular-nav-button--next">${nextBtn}</div>
    `;

    sliderContainer.querySelectorAll('.el-circular-nav-button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    wrapper.appendChild(sliderContainer);

    const initialSlide = 0;

    let isSwiping = false;

    sliderContainer.addEventListener(
      'click',
      (e) => {
        if (isSwiping) {
          e.preventDefault();
          e.stopPropagation();
          isSwiping = false;
        }
      },
      true,
    );

    const swiperInstance = new Swiper(sliderContainer, {
      modules: [Navigation],
      spaceBetween: 4,
      slidesPerView: 1,
      loop: true,
      nested: true,
      grabCursor: true,
      initialSlide: initialSlide,
      navigation: {
        nextEl: '.el-circular-nav-button--next',
        prevEl: '.el-circular-nav-button--prev',
      },
      preventClicks: false,
      preventClicksPropagation: false,
      touchStartPreventDefault: false,
      on: {
        sliderMove: () => {
          isSwiping = true;
        },
        touchEnd: () => {
          setTimeout(() => {
            isSwiping = false;
          }, 50);
        },
      },
    });

    this.activeProductSlider = { swiper: swiperInstance, wrapper: wrapper };
  }

  destroyProductSlider() {
    if (!this.activeProductSlider) {
      return;
    }

    const { swiper, wrapper } = this.activeProductSlider;

    const vimeoId = wrapper.querySelector('.js-slider-data')?.dataset.vimeoId;
    if (vimeoId) {
      wrapper.querySelector('.js-video-container')?.style.removeProperty('display');
    } else {
      wrapper.querySelector('.js-main-image')?.style.removeProperty('display');
    }

    swiper.destroy(true, true);
    wrapper.querySelector('.el-you-may-also-like-item__inner-swiper')?.remove();
    this.activeProductSlider = null;
  }
};

elabs.onJavascriptReady(() => {
  const youMayAlsoLikeElements = document.querySelectorAll('.el-you-may-also-like');
  youMayAlsoLikeElements.forEach((element) => {
    void new elabs.YouMayAlsoLike(element);
  });
});
